import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";

interface ActivityLogProps {
  caseId: string;
}

interface ActivityLogEntry {
  id: string;
  activity_type: string;
  old_value: string | null;
  new_value: string | null;
  triggered_by: string;
  metadata: any;
  created_at: string;
}

// Mapping des types d'activité vers des icônes et labels
const ACTIVITY_CONFIG: Record<string, { icon: string; label: (log: ActivityLogEntry) => string; color: string }> = {
  document_archived: { icon: "🗄️", label: (log) => `${log.metadata?.document_type === "quotes" ? "Devis" : "Facture"} v${log.metadata?.version} archivé`, color: "text-gray-600" },
  document_restored: { icon: "↩️", label: (log) => `${log.metadata?.document_type === "quotes" ? "Devis" : "Facture"} v${log.metadata?.version} restauré`, color: "text-indigo-700" },
  status_change: {
    icon: "🔄",
    label: (log) => `Statut changé : "${log.old_value}" → "${log.new_value}"`,
    color: "text-blue-700",
  },
  quote_uploaded: {
    icon: "📄",
    label: (log) => {
      const version = log.metadata?.version;
      return `Devis ${version ? `v${version}` : ""} uploadé`;
    },
    color: "text-purple-700",
  },
  quote_sent: {
    icon: "📧",
    label: () => "Email envoyé avec devis",
    color: "text-indigo-700",
  },
  quote_signed: {
    icon: "✍️",
    label: () => "Devis signé par le client",
    color: "text-green-700",
  },
  quote_status_changed: {
    icon: "🔄",
    label: (log) => `Statut devis changé : "${log.old_value}" → "${log.new_value}"`,
    color: "text-blue-700",
  },
  invoice_uploaded: {
    icon: "🧾",
    label: (log) => {
      const type = log.metadata?.invoice_type === "deposit" ? "d'acompte" : "finale";
      return `Facture ${type} uploadée`;
    },
    color: "text-amber-700",
  },
  invoice_sent: {
    icon: "📧",
    label: () => "Email envoyé avec facture",
    color: "text-indigo-700",
  },
  email_sent: {
    icon: "📧",
    label: () => "Email envoyé",
    color: "text-indigo-700",
  },
  email_received: {
    icon: "📥",
    label: () => "Email reçu du client",
    color: "text-green-700",
  },
  reminder_quote_sent: {
    icon: "🔔",
    label: () => "Relance automatique envoyée (devis)",
    color: "text-orange-700",
  },
  reminder_invoice_sent: {
    icon: "🔔",
    label: () => "Relance automatique envoyée (facture)",
    color: "text-orange-700",
  },
  task_created: {
    icon: "🔥",
    label: (log) => {
      const who = log.metadata?.assigned_center ? ` pour l'équipe ${log.metadata.assigned_center}` : log.metadata?.assigned_to ? " (pour le manager)" : "";
      const note = log.metadata?.note ? ` — « ${log.metadata.note} »` : "";
      return `Tâche assignée : ${taskLabelOf(log)}${who}${note}`;
    },
    color: "text-orange-700",
  },
  task_completed: { icon: "✅", label: (log) => `Tâche faite : ${taskLabelOf(log)}`, color: "text-green-700" },
  task_reopened: { icon: "↩️", label: (log) => `Tâche rouverte : ${taskLabelOf(log)}`, color: "text-indigo-700" },
  task_cancelled: { icon: "🚫", label: (log) => `Tâche annulée : ${taskLabelOf(log)}`, color: "text-gray-600" },
  priority_set: { icon: "🚩", label: () => "Dossier marqué prioritaire", color: "text-red-700" },
  priority_removed: { icon: "🏳️", label: () => "Priorité retirée", color: "text-gray-600" },
};

function taskLabelOf(log: ActivityLogEntry): string {
  return log.metadata?.task_label || log.new_value || "tâche";
}

export function ActivityLog({ caseId }: ActivityLogProps) {
  const [logs, setLogs] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("case_activity_log")
        .select("*")
        .eq("case_id", caseId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Erreur lors du chargement des logs:", error);
      } else {
        setLogs(data || []);
      }
      setLoading(false);
    };

    fetchLogs();
  }, [caseId]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTriggeredByLabel = (triggeredBy: string) => {
    if (triggeredBy === "system:auto") return "Système";
    if (triggeredBy === "client") return "Client";
    if (triggeredBy.startsWith("user:")) {
      const email = triggeredBy.replace("user:", "");
      return email;
    }
    return triggeredBy;
  };

  // Grouper les logs par date
  const groupedLogs: Record<string, ActivityLogEntry[]> = {};
  logs.forEach((log) => {
    const dateKey = formatDate(log.created_at);
    if (!groupedLogs[dateKey]) {
      groupedLogs[dateKey] = [];
    }
    groupedLogs[dateKey].push(log);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 text-gray-500">
        Chargement de l'historique...
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-gray-500">
        <div className="text-4xl mb-3">📋</div>
        <p className="text-sm">Aucune activité pour le moment</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {Object.entries(groupedLogs).map(([date, dateLogs]) => (
        <div key={date}>
          {/* Date header */}
          <div className="sticky top-0 bg-white z-10 pb-2">
            <h3 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2">
              📅 {date}
            </h3>
          </div>

          {/* Timeline des logs */}
          <div className="mt-4 space-y-3">
            {dateLogs.map((log) => {
              const config = ACTIVITY_CONFIG[log.activity_type] || {
                icon: "📌",
                label: () => log.activity_type,
                color: "text-gray-700",
              };

              return (
                <div
                  key={log.id}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {/* Time */}
                  <div className="flex-shrink-0 w-12 text-xs text-gray-500 font-mono pt-1">
                    {formatTime(log.created_at)}
                  </div>

                  {/* Icon */}
                  <div className="flex-shrink-0 text-xl">
                    {config.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${config.color}`}>
                      {config.label(log)}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      par {getTriggeredByLabel(log.triggered_by)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
